import { useCallback, useMemo, useState } from "react";
import { useSortBy, useTable } from "react-table";
import styled from "styled-components";
import currency from "currency.js";
import {
  Align,
  TableHeaderCell,
  TableWrapper,
  TitleWrapper,
  DashboardRow,
} from "../Table/style";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import { ITableColumn } from "../Table/TableColumn";
import { SPACING } from "../../constants/design/spacing";
import {
  COLORS,
  TEXT_COLORS,
  BACKGROUND_COLORS,
  BORDER_COLORS,
  LAYER_COLORS,
} from "../../constants/design/colors";
import { FONT_SIZE, FONT_STYLE } from "../../constants/design/fontSize";
import { Button, ButtonThemeEnum } from "../shared/Buttons/styles";
import { Spinner } from "../shared/Spinner";
import {
  useStoreBuybackAndBurns,
  IStoreBuybackAndBurnRecord,
} from "../../hooks/api/staking/useStoreBuybackAndBurns";
import {
  useBuybacksAndBurns,
  IBuybackAndBurnRecord,
} from "../../hooks/api/staking/useBuybacksAndBurns";

const FormWrapper = styled.div`
  background: ${LAYER_COLORS.two};
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 8px;
  padding: ${SPACING.three}px;
  margin-bottom: ${SPACING.four}px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${SPACING.three}px;
  margin-bottom: ${SPACING.three}px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.one}px;
`;

const Label = styled.label`
  color: ${TEXT_COLORS.two};
  font-size: ${FONT_SIZE.one};
`;

const FormInput = styled.input<{ hasErrors?: boolean }>`
  background: ${BACKGROUND_COLORS.four};
  border: 1px solid
    ${({ hasErrors }) => (hasErrors ? COLORS.red.one : BORDER_COLORS.one)};
  border-radius: 4px;
  color: ${TEXT_COLORS.one};
  font-size: ${FONT_SIZE.two};
  padding: ${SPACING.two}px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ hasErrors }) =>
      hasErrors ? COLORS.red.one : COLORS.blue.one};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormSelect = styled.select<{ hasErrors?: boolean }>`
  background: ${BACKGROUND_COLORS.four};
  border: 1px solid
    ${({ hasErrors }) => (hasErrors ? COLORS.red.one : BORDER_COLORS.one)};
  border-radius: 4px;
  color: ${TEXT_COLORS.one};
  font-size: ${FONT_SIZE.two};
  padding: ${SPACING.two}px;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ hasErrors }) =>
      hasErrors ? COLORS.red.one : COLORS.blue.one};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.span`
  color: ${COLORS.red.one};
  font-size: ${FONT_SIZE.xs};
`;

interface FormState {
  month: string;
  totalVolume: string;
  tier: string;
  burnAmount: string;
  buybackAmount: string;
}

interface FormErrors {
  month?: string;
  totalVolume?: string;
  tier?: string;
  burnAmount?: string;
  buybackAmount?: string;
}

const TIER_OPTIONS = ["1", "2", "3", "4", "5"];

export function BuybacksAndBurns() {
  const { storeBuybackAndBurns } = useStoreBuybackAndBurns();
  const { data: records, isLoading, mutate } = useBuybacksAndBurns();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    month: "",
    totalVolume: "",
    tier: "1",
    burnAmount: "",
    buybackAmount: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Validate month (format: YYYY-MM-01)
    if (!formState.month) {
      newErrors.month = "Month is required";
    } else {
      const dateRegex = /^\d{4}-\d{2}-01$/;
      if (!dateRegex.test(formState.month)) {
        newErrors.month = "Format must be YYYY-MM-01 (e.g., 2026-01-01)";
      }
    }

    // Validate total volume
    if (!formState.totalVolume) {
      newErrors.totalVolume = "Total volume is required";
    } else {
      const volume = Number(formState.totalVolume);
      if (Number.isNaN(volume) || volume < 0) {
        newErrors.totalVolume = "Must be a valid positive number";
      }
    }

    // Validate tier
    if (!formState.tier) {
      newErrors.tier = "Tier is required";
    }

    // Validate burn amount
    if (!formState.burnAmount) {
      newErrors.burnAmount = "Burn amount is required";
    } else {
      const burn = Number(formState.burnAmount);
      if (Number.isNaN(burn) || burn < 0) {
        newErrors.burnAmount = "Must be a valid positive number";
      }
    }

    // Validate buyback amount
    if (!formState.buybackAmount) {
      newErrors.buybackAmount = "Buyback amount is required";
    } else {
      const buyback = Number(formState.buybackAmount);
      if (Number.isNaN(buyback) || buyback < 0) {
        newErrors.buybackAmount = "Must be a valid positive number";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState]);

  const handleInputChange = useCallback(
    (field: keyof FormState, value: string) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const currentTimestamp = Date.now();
    const timestampNanoseconds = currentTimestamp * 1e6;

    const record: IStoreBuybackAndBurnRecord = {
      Month: formState.month,
      TotalVolume: Number(formState.totalVolume),
      Tier: formState.tier,
      BurnAmount: Number(formState.burnAmount),
      BuybackAmount: Number(formState.buybackAmount),
      _CreatedTimestamp: timestampNanoseconds,
    };

    const success = await storeBuybackAndBurns([record]);

    if (success) {
      // Reset form on success
      setFormState({
        month: "",
        totalVolume: "",
        tier: "1",
        burnAmount: "",
        buybackAmount: "",
      });
      // Refresh the table data
      mutate();
    }

    setIsSubmitting(false);
  }, [formState, validateForm, storeBuybackAndBurns, mutate]);

  const formatCurrency = useCallback(
    (value: string | number) =>
      value
        ? currency(Number(value), { precision: 0 }).format({ symbol: "$" })
        : "-",
    []
  );

  const formatAmount = useCallback(
    (value: number) =>
      currency(value, { precision: 0 }).format({ symbol: "" }),
    []
  );

  const formatMonth = useCallback((month: string) => {
    try {
      const date = new Date(month);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
      });
    } catch {
      return month;
    }
  }, []);

  const isFormValid =
    formState.month &&
    formState.totalVolume &&
    formState.tier &&
    formState.burnAmount &&
    formState.buybackAmount;

  const columns: ITableColumn<IBuybackAndBurnRecord>[] = useMemo(
    () => [
      {
        title: "Month",
        align: "left",
        accessor: "month",
        Cell: ({ value }: any) => <span>{formatMonth(value)}</span>,
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const dateA = new Date(a.original.month).getTime();
          const dateB = new Date(b.original.month).getTime();
          if (dateA > dateB) return 1;
          if (dateA < dateB) return -1;
          return 0;
        },
      },
      {
        title: "Total Volume",
        align: "right",
        accessor: "totalVolume",
        Cell: ({ value }: any) => (
          <Align align="right">{formatCurrency(value)}</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const volumeA = Number(a.original.totalVolume);
          const volumeB = Number(b.original.totalVolume);
          if (volumeA > volumeB) return 1;
          if (volumeA < volumeB) return -1;
          return 0;
        },
      },
      {
        title: "Tier",
        align: "center",
        accessor: "tier",
        Cell: ({ value }: any) => <Align align="center">{value}</Align>,
        canSort: true,
      },
      {
        title: "Burn Amount",
        align: "right",
        accessor: "burnAmount",
        Cell: ({ value }: any) => (
          <Align align="right">{formatAmount(value)} AEVO</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const burnA = Number(a.original.burnAmount);
          const burnB = Number(b.original.burnAmount);
          if (burnA > burnB) return 1;
          if (burnA < burnB) return -1;
          return 0;
        },
      },
      {
        title: "Buyback Amount",
        align: "right",
        accessor: "buybackAmount",
        Cell: ({ value }: any) => (
          <Align align="right">{formatAmount(value)} AEVO</Align>
        ),
        canSort: true,
        sortDescFirst: true,
        sortType: (a: any, b: any) => {
          const buybackA = Number(a.original.buybackAmount);
          const buybackB = Number(b.original.buybackAmount);
          if (buybackA > buybackB) return 1;
          if (buybackA < buybackB) return -1;
          return 0;
        },
      },
    ],
    [formatMonth, formatCurrency, formatAmount]
  );

  const memoizedRecords = useMemo(() => records || [], [records]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: memoizedRecords,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        initialState: {
          sortBy: [
            {
              id: "month",
              desc: true,
            },
          ],
        },
        autoResetSortBy: false,
      },
      useSortBy
    );

  // Calculate totals
  const totals = useMemo(() => {
    if (!records || records.length === 0) return null;
    return {
      totalVolume: records.reduce((sum, r) => sum + r.totalVolume, 0),
      totalBurn: records.reduce((sum, r) => sum + r.burnAmount, 0),
      totalBuyback: records.reduce((sum, r) => sum + r.buybackAmount, 0),
    };
  }, [records]);

  return (
    <>
      <TitleWrapper>
        <h3>Add Buyback & Burn Record</h3>
      </TitleWrapper>

      <p style={{ ...FONT_STYLE.label.one, marginBottom: SPACING.three }}>
        Enter the monthly buyback and burn statistics to store in the database.
      </p>

      <FormWrapper>
        <FormGrid>
          <FormField>
            <Label htmlFor="month">Month (YYYY-MM-01)</Label>
            <FormInput
              id="month"
              type="text"
              placeholder="2026-01-01"
              value={formState.month}
              onChange={(e) => handleInputChange("month", e.target.value)}
              hasErrors={!!errors.month}
            />
            {errors.month && <ErrorText>{errors.month}</ErrorText>}
          </FormField>

          <FormField>
            <Label htmlFor="totalVolume">Total Volume (USD)</Label>
            <FormInput
              id="totalVolume"
              type="number"
              placeholder="144000000"
              value={formState.totalVolume}
              onChange={(e) => handleInputChange("totalVolume", e.target.value)}
              hasErrors={!!errors.totalVolume}
            />
            {errors.totalVolume && <ErrorText>{errors.totalVolume}</ErrorText>}
          </FormField>

          <FormField>
            <Label htmlFor="tier">Tier</Label>
            <FormSelect
              id="tier"
              value={formState.tier}
              onChange={(e) => handleInputChange("tier", e.target.value)}
              hasErrors={!!errors.tier}
            >
              {TIER_OPTIONS.map((tier) => (
                <option key={tier} value={tier}>
                  Tier {tier}
                </option>
              ))}
            </FormSelect>
            {errors.tier && <ErrorText>{errors.tier}</ErrorText>}
          </FormField>

          <FormField>
            <Label htmlFor="burnAmount">Burn Amount (AEVO)</Label>
            <FormInput
              id="burnAmount"
              type="number"
              placeholder="1000000"
              value={formState.burnAmount}
              onChange={(e) => handleInputChange("burnAmount", e.target.value)}
              hasErrors={!!errors.burnAmount}
            />
            {errors.burnAmount && <ErrorText>{errors.burnAmount}</ErrorText>}
          </FormField>

          <FormField>
            <Label htmlFor="buybackAmount">Buyback Amount (AEVO)</Label>
            <FormInput
              id="buybackAmount"
              type="number"
              placeholder="1000000"
              value={formState.buybackAmount}
              onChange={(e) =>
                handleInputChange("buybackAmount", e.target.value)
              }
              hasErrors={!!errors.buybackAmount}
            />
            {errors.buybackAmount && (
              <ErrorText>{errors.buybackAmount}</ErrorText>
            )}
          </FormField>
        </FormGrid>

        <Button
          buttonTheme={ButtonThemeEnum.HIGHLIGHT}
          onClick={handleSubmit}
          disabled={isSubmitting || !isFormValid}
        >
          {isSubmitting ? "Storing..." : "Store Buyback & Burn Record"}
        </Button>
      </FormWrapper>

      <TitleWrapper>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <h3>
            Buybacks & Burns History{" "}
            {memoizedRecords.length > 0 ? `(${memoizedRecords.length})` : ""}
          </h3>
        </div>
      </TitleWrapper>

      {totals && (
        <div
          style={{
            display: "flex",
            gap: SPACING.four,
            color: TEXT_COLORS.one,
            marginBottom: SPACING.three,
          }}
        >
          <div>
            <strong>Total Volume:</strong> {formatCurrency(totals.totalVolume)}
          </div>
          <div>
            <strong>Total Burned:</strong> {formatAmount(totals.totalBurn)} AEVO
          </div>
          <div>
            <strong>Total Buyback:</strong> {formatAmount(totals.totalBuyback)}{" "}
            AEVO
          </div>
        </div>
      )}

      <TableWrapper>
        {isLoading ? <Spinner /> : null}
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              // eslint-disable-next-line react/jsx-key
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  // eslint-disable-next-line react/jsx-key
                  <TableHeaderCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{ width: column.width }}
                  >
                    {column.render("Header")}
                  </TableHeaderCell>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const { ...rowProps } = row.getRowProps();
              return (
                // eslint-disable-next-line react/jsx-key
                <DashboardRow {...rowProps} hasRecord={false}>
                  {row.cells.map((cell) => (
                    // eslint-disable-next-line react/jsx-key
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </DashboardRow>
              );
            })}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                  No buyback and burn records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableWrapper>
    </>
  );
}
